import { createContext, useContext, useMemo, useState } from "react";

import {
  autoUpdate,
  flip,
  offset,
  type Placement,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";

type HoverPopoverLockApi = {
  setLocked: (locked: boolean) => void;
};

const HoverPopoverLockContext = createContext<HoverPopoverLockApi | null>(null);

export const HoverPopoverLockProvider = HoverPopoverLockContext.Provider;

export const useHoverPopoverLock = (): HoverPopoverLockApi | null =>
  useContext(HoverPopoverLockContext);

type UseHoverPopoverOptions = {
  enabled: boolean;
  placement?: Placement;
  offsetPx?: number;
  openDelayMs?: number;
  closeDelayMs?: number;
};

export const useHoverPopover = ({
  enabled,
  placement = "right-start",
  offsetPx = 12,
  openDelayMs = 100,
  closeDelayMs = 100,
}: UseHoverPopoverOptions) => {
  const [hoverIsOpen, setHoverIsOpen] = useState(false);
  const [childOpen, setChildOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: hoverIsOpen,
    onOpenChange: setHoverIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(offsetPx), flip(), shift({ padding: 8 })],
  });

  const hover = useHover(context, {
    enabled,
    delay: { open: openDelayMs, close: closeDelayMs },
    handleClose: safePolygon(),
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
  ]);

  // Keeps the popover open while a portaled child (e.g. an options menu
  // rendered outside the floating subtree) is open — `safePolygon` can't see
  // those children, so without this the parent would close as soon as the
  // cursor crossed into them.
  const lockApi = useMemo<HoverPopoverLockApi>(
    () => ({ setLocked: setChildOpen }),
    [],
  );

  // Reset state when `enabled` flips false so a stale `hoverIsOpen` /
  // `childOpen` doesn't pop the popover back open on re-enable. Done as a
  // prev-value comparison during render rather than a useEffect — React
  // discards the render and retries with the reset state.
  const [prevEnabled, setPrevEnabled] = useState(enabled);
  if (prevEnabled !== enabled) {
    setPrevEnabled(enabled);
    if (!enabled) {
      setHoverIsOpen(false);
      setChildOpen(false);
    }
  }

  return {
    isOpen: enabled && (hoverIsOpen || childOpen),
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    lockApi,
  };
};
